import { z } from 'zod';
import { timePadding } from '@/lib/datetime';
import type { MessageTransform } from '@/store/signageStore';

/**
 * Per-widget-type payload transforms, replacing the legacy per-widget Vuex modules.
 *
 * Almost every legacy widget store (`textWidgetStore`, `doughnutWidgetStore`,
 * `topSpacesStore`, `travelStore`, `floorOccupancyStore`, `hourlyChartStore`,
 * `bookingsWidgetStore`, …) is a byte-identical pass-through: `messageRecieved` [sic] commits
 * the payload unchanged. Those get no entry here — the *absence* of a transform is the
 * pass-through (docs/MIGRATION.md §4). Only two legacy stores actually shaped their data,
 * and both are ported below.
 *
 * Every transform Zod-validates its input and degrades to an empty result on a mismatch
 * rather than throwing. A malformed retained MQTT message is a normal event on a live
 * broker; it must cost a widget its contents, never the page (CLAUDE.md constraint 4).
 */

/* ---------------------------------- WeatherWidget --------------------------------------- */

/**
 * `dt` is `.int()` deliberately. The legacy code computed milliseconds as
 * `Number(\`${item.dt}000\`)` — string-concatenating three zeros onto a Unix *seconds*
 * timestamp — which equals `item.dt * 1000` only for integer `dt`. The multiplication below
 * is the readable form of the same thing, and this constraint is what keeps the two
 * expressions equivalent: a fractional `dt` is not a timestamp we recognise, so the payload
 * is rejected instead of silently rendering a different hour than the legacy app would.
 */
const weatherHourSchema = z.object({
  temp: z.number(),
  icon: z.string(),
  /** Unix seconds. */
  dt: z.number().int(),
});

const weatherPayloadSchema = z.object({
  hourly: z.array(weatherHourSchema),
});

/** One column of the weather widget. */
export type WeatherHour = {
  temp: number;
  icon: string;
  /** Zero-padded local hour, e.g. `'07'`. */
  hour: string;
};

/**
 * `data.hourly` -> the next six hours as `{ temp, icon, hour }`.
 *
 * Ported from `weatherWidgetStore.ts`. Fewer than six entries in `hourly` yields fewer
 * columns, exactly as the legacy `slice(0, 6)` did.
 */
export function weatherTransform(raw: unknown): WeatherHour[] {
  const parsed = weatherPayloadSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[signage] weather payload rejected', z.prettifyError(parsed.error));
    return [];
  }

  return parsed.data.hourly.slice(0, 6).map((item) => ({
    temp: item.temp,
    icon: item.icon,
    hour: timePadding(new Date(item.dt * 1000).getHours()),
  }));
}

/* --------------------------------- DepartureWidget -------------------------------------- */

/**
 * `{ [station]: { [line]: { [heading]: string[] } } }` — three levels of free-form keys,
 * with ISO-ish date strings at the leaves. The legacy store walked it with nested
 * `Object.keys` loops and no validation at all.
 */
const departurePayloadSchema = z.record(
  z.string(),
  z.record(z.string(), z.record(z.string(), z.array(z.string()))),
);

export type DepartureHeading = {
  heading: string;
  /** `HH:MM`, local time. */
  times: string[];
};

export type DepartureLine = {
  name: string;
  headings: DepartureHeading[];
};

export type DepartureStation = {
  name: string;
  transports: DepartureLine[];
};

/**
 * Departure board payload -> one entry per station, each with its lines and headings.
 *
 * Ported from `departureWidgetStore.ts`, whose output shape is documented by the dead
 * `transports: [...]` literal left at the bottom of that file. Insertion order of the config
 * keys is preserved, which is the order the board is rendered in.
 *
 * One deliberate deviation (docs/MIGRATION.md §4, decision #14): the legacy code fed every
 * leaf string to `new Date(...)` and formatted the result unconditionally, so an unparseable
 * value rendered as the literal `NaN:NaN` on the wall. Unparseable values are dropped here
 * and warned about instead. Real payloads never hit this path.
 */
export function departureTransform(raw: unknown): DepartureStation[] {
  const parsed = departurePayloadSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[signage] departure payload rejected', z.prettifyError(parsed.error));
    return [];
  }

  return Object.entries(parsed.data).map(([stationName, lines]) => ({
    name: stationName,
    transports: Object.entries(lines).map(([lineName, headings]) => ({
      name: lineName,
      headings: Object.entries(headings).map(([heading, times]) => ({
        heading,
        times: times.map(toClockTime).filter((time): time is string => time !== null),
      })),
    })),
  }));
}

/** `'2024-05-01T19:30:00Z'` -> `'19:30'` in local time; `null` when unparseable. */
function toClockTime(value: string): string | null {
  const departure = new Date(value);
  if (Number.isNaN(departure.getTime())) {
    console.warn('[signage] departure time is not a parseable date, dropped:', value);
    return null;
  }
  return `${timePadding(departure.getHours())}:${timePadding(departure.getMinutes())}`;
}

/* ------------------------------------- Registry ----------------------------------------- */

/**
 * `component.type` -> transform. SYM-491's `ModuleLoader` looks a widget's transform up here
 * when it registers the widget's topic; every type that is absent is a pass-through.
 *
 * The keys are config values (`"type": "WeatherWidget"`) and are therefore as immovable as
 * any other config key (CLAUDE.md constraint 1).
 */
export const transformsByWidgetType: Record<string, MessageTransform> = {
  WeatherWidget: weatherTransform,
  DepartureWidget: departureTransform,
};
